a<template>

<div qid="integrity-check-section"
     id="update_data_processor_agreement"
>
  <div class="card">
    <div class="card-header">
      <div class="float-right">
        <span class="btn btn-success"
              @click="showFormFunction"
              qid="risk-edit-button"
              v-if="isGranted([permissions.THIRD_PARTY_EDIT, permissions.THIRD_PARTY_EDIT_OWN], thirdParty) && (!getClient.parent || (getClient.parent && thirdParty.partition))"
        >
          <font-awesome-icon icon="pen"/> {{ $t('system.edit') }}
        </span>
      </div>
      <div class="float-left">
        <h5 class="mb-2" qid="integrity-check-table-title" v-if="!showOnTask">
          {{ $t('maintenance.third_parties.tab.dpa') }}
        </h5>
        <span v-if="!showOnTask">
          <span class="cursor-pointer task-label-open mr-2"
                v-if="getTask('update_data_processor_agreement')"
          >
            <UpdateSingleTaskModal
                :task="getTask('update_data_processor_agreement')"
                @reloadContent="reloadContent"
                :editIcon="true"
            ></UpdateSingleTaskModal>
          </span>
          <span v-if="getGuide('update_data_processor_agreement')">
            <GuideButton :guide="getGuide('update_data_processor_agreement')"></GuideButton>
          </span>
        </span>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-12">
        <div class="card-body pb-0">
          <div class="mw-500" v-if="showForm">
            <label for="">{{ $t('maintenance.third_parties.create.data_processor_label') }}</label>
            <multiselect
                v-model="form.data_processor_agreement"
                :options="dpaItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.third_parties.create.data_processor_placeholder')"
                label="name"
                qid="edit-tp-form-data-processor"
            >
              <template slot="option" slot-scope="props">
                <font-awesome-icon icon="check-circle" class="icon-success" v-if="props.option.value == 'Yes'" style="background:white;border-radius:50%;"/>
                <font-awesome-icon icon="info-circle" class="icon-info" v-if="props.option.value == 'N/A'" style="background:white;border-radius:50%;"/>
                <font-awesome-icon icon="times-circle" class="icon-danger" v-if="props.option.value == 'No'" style="background:white;border-radius:50%;"/>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
              <template slot="singleLabel" slot-scope="props">
                <font-awesome-icon icon="check-circle" class="icon-success" v-if="props.option.value == 'Yes'" style="background:white;border-radius:50%;"/>
                <font-awesome-icon icon="info-circle" class="icon-info" v-if="props.option.value == 'N/A'" style="background:white;border-radius:50%;"/>
                <font-awesome-icon icon="times-circle" class="icon-danger" v-if="props.option.value == 'No'" style="background:white;border-radius:50%;"/>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>
          </div>
          <div v-if="!showForm">
            <div v-if="thirdParty">
              <label for="">{{ $t('maintenance.third_parties.create.data_processor_label') }}</label>
              <p qid="risk-dpia" v-if="thirdParty.data_processor_agreement" class="section-container">
                <span qid="tp-inspection">
                  <font-awesome-icon icon="check-circle" class="icon-success mr-1" v-if="thirdParty.data_processor_agreement == 'Yes'"/>
                  <font-awesome-icon icon="info-circle" class="icon-info mr-1" v-if="thirdParty.data_processor_agreement == 'N/A'"/>
                  <font-awesome-icon icon="times-circle" class="icon-danger mr-1" v-if="thirdParty.data_processor_agreement == 'No'"/>
                  {{ $t('maintenance.third_parties.data_processor_agreement.' + $options.filters.slugify(thirdParty.data_processor_agreement)) }}
                </span>
              </p>
              <p v-if="!thirdParty.data_processor_agreement" class="text-muted section-container">
                {{ $t('system.missing_input') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-12">
        <div class="card-body pb-0">
          <div class="mw-500" v-if="showForm">
            <label for="">{{ $t('maintenance.third_parties.create.contract_date_label') }}</label>
            <datepicker
                class="date-picker"
                qid="edit-tp-form-dpa-updated-at"
                v-model="form.dpa_updated_at"
                :clear-button="true"
                :placeholder="$t('maintenance.third_parties.create.contract_date_placeholder')"
            ></datepicker>
          </div>
          <div v-if="!showForm">
            <div v-if="thirdParty">
              <label for="">{{ $t('maintenance.third_parties.create.contract_date_label') }}</label>
              <p qid="risk-dpia" v-if="thirdParty.dpa_updated_at" class="section-container">
                {{ thirdParty.dpa_updated_at | formatDate }}
              </p>
              <p v-if="!thirdParty.dpa_updated_at" class="text-muted section-container">
                {{ $t('system.missing_input') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="!showForm">
      <div class="col-sm-12">
        <div class="card-body pb-0">
          <div>
            <div v-if="thirdParty">
              <label for="">{{ $t('maintenance.third_parties.create.processing_role_label') }}</label>
              <p qid="risk-dpia" v-if="thirdParty.processing_roles && thirdParty.processing_roles.length" class="section-container">
                <span qid="tp-inspection">
                  <span class="badge mr-1" v-for="processing_role in thirdParty.processing_roles">
                    <span v-if="processing_role == 'Data processor'">
                      <font-awesome-icon icon="microchip" class="mr-2"/>
                    </span>
                    <span v-if="processing_role == 'Data controller'">
                      <font-awesome-icon icon="joystick" class="mr-2"/>
                    </span>
                    <span v-if="processing_role == 'Joint controller'">
                      <font-awesome-icon icon="handshake" class="mr-2"/>
                    </span>
                    {{ $t('maintenance.third_parties.processing_role.' + $options.filters.slugify(processing_role)) }}
                  </span>
                </span>
              </p>
              <p v-if="!thirdParty.processing_roles.length" class="text-muted section-container">
                {{ $t('system.missing_input') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-12">
        <div class="card-body pb-0">
          <div class="mw-500" v-if="showForm">
            <label for="">{{ $t('maintenance.third_parties.create.dpa_description_label') }}</label>
            <b-form-textarea
              qid="risk-action-description"
              v-model="form.dpa_description"
              rows="4"
              :placeholder="$t('maintenance.third_parties.create.dpa_description_placeholder')"
            ></b-form-textarea>
          </div>
          <div v-if="!showForm">
            <label for="">{{ $t('maintenance.third_parties.create.dpa_description_label') }}</label>
            <p class="text-formatted section-container"
               qid="risk-action-plan"
               style="min-height: 105px;"
               v-if="thirdParty.dpa_description"
            >{{ thirdParty.dpa_description }}</p>
            <p v-if="!thirdParty.dpa_description" class="text-muted section-container">
              {{ $t('system.missing_input') }}
            </p>
          </div>
        </div>
      </div>
    </div>


    <div class="row" v-if="!showForm">
      <div class="col-sm-12">
        <div class="card-body pb-0">
          <div>
            <label for="">{{ $t('maintenance.third_parties.create.location_of_legal_entity_label') }}</label>
            <div v-if="thirdParty">
              <div qid="risk-dpia" v-if="thirdParty.vendor_countries && thirdParty.vendor_countries.length" class="section-container">
                <span class="badge" v-for="country in thirdParty.vendor_countries">
                  <img v-bind:src="country.flag" height="13" class="align-baseline mr-1" /> <span>{{country.name}}</span>
                </span>
              </div>
            </div>
            <p v-if="!thirdParty.vendor_countries.length" class="text-muted section-container">
              {{ $t('system.missing_input') }}
            </p>
            <div class="text-muted text-small">{{ $t('maintenance.third_parties.create.location_of_legal_entity_info') }}</div>
          </div>
        </div>
      </div>
    </div>


    <div class="row">
      <div class="col-sm-12">
        <div class="card-body pb-0">
          <div class="mw-500" v-if="showForm">
            <label for="">{{ $t('maintenance.third_parties.create.country_label') }}</label>
            <multiselect
                v-model="selectedCountries"
                :options="countries"
                :multiple="true"
                :taggable="false"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                track-by="name"
                :placeholder="$t('maintenance.third_parties.create.country_placeholder')"
                label="name"
                qid="edit-tp-countries"
            >
              <template slot="tag" slot-scope="props">
                <img v-bind:src="props.option.flag" height="13" class="align-baseline" />
                <span class="mr-2">
                  {{ props.option.name }}
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <img v-bind:src="props.option.flag" height="13" class="align-baseline" />
                <span class="mr-2">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>
            <div class="text-muted text-small">{{ $t('maintenance.third_parties.create.country_info') }}</div>
          </div>
          <div v-if="!showForm">
            <div v-if="thirdParty">
              <label for="">{{ $t('maintenance.third_parties.create.country_label') }}</label>
              <div qid="risk-dpia" v-if="thirdParty.processing_locations && thirdParty.processing_locations.length" class="section-container">
                <span class="badge" v-for="country in thirdParty.processing_locations">
                  <img v-bind:src="country.flag" height="13" class="align-baseline mr-1" v-if="country.flag"/> <span>{{country.name}}</span>
                </span>
              </div>
              <p v-if="!thirdParty.processing_locations.length" class="text-muted section-container">
                {{ $t('system.missing_input') }}
              </p>
              <div class="text-muted text-small">{{ $t('maintenance.third_parties.create.country_info') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div class="row">
      <div class="col-sm-12">
        <div class="card-body pb-0">
          <div class="mw-500" v-if="showForm">
            <label for="">{{ $t('maintenance.third_parties.create.dpa_contract_type_label') }}</label>
            <multiselect
                v-model="form.third_country_contract_type"
                :options="thirdCountryContractTypes"
                :placeholder="$t('maintenance.third_parties.create.dpa_contract_type_label')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="create-tp-form-data-processor"
            ></multiselect>
          </div>
          <div v-if="!showForm">
            <div v-if="thirdParty">
              <label for="">{{ $t('maintenance.third_parties.create.dpa_contract_type_label') }}</label>
              <p qid="tp-inspection" v-if="thirdParty.third_country_contract_type" class="section-container">
                {{ $t('maintenance.third_parties.third_country_contract_type.' + $options.filters.slugify(thirdParty.third_country_contract_type)) }}
              </p>
              <p v-if="!thirdParty.third_country_contract_type" class="text-muted section-container">
                {{ $t('system.missing_input') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div class="row">
      <div class="col-sm-12">
        <div class="card-body pb-0">
          <div class="mw-500" v-if="showForm">
            <label for="">{{ $t('maintenance.third_parties.create.inspection_label') }}</label>
            <multiselect
                v-model="form.inspection"
                :options="inspectionItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.third_parties.create.inspection_placeholder')"
                label="name"
                qid="edit-tp-form-inspection"
            ></multiselect>
            <div class="text-right mb-3">
              <b-button class="mt-3"
                        variant="secondary"
                        @click="cancelForm"
                        qid="risk-form-cancel-button"
              >
                {{ $t('processes.legal_basis.create.cancel') }}
              </b-button>
              <b-button class="mt-3 ml-3"
                        variant="success"
                        @click="formSubmit"
                        qid="risk-form-submit-button"
              >
                {{ $t('processes.legal_basis.create.submit') }}
              </b-button>
            </div>
          </div>
          <div v-if="!showForm">
            <div v-if="thirdParty">
              <label for="">{{ $t('maintenance.third_parties.create.inspection_label') }}</label>
              <p qid="risk-dpia" v-if="thirdParty.inspection" class="section-container">
                <span qid="tp-inspection">
                  {{ $t('maintenance.third_parties.inspection.' + $options.filters.slugify(thirdParty.inspection)) }}
                </span>
              </p>
              <p v-if="!thirdParty.inspection" class="text-muted section-container">
                {{ $t('system.missing_input') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

  <ConfirmRemoveAttachment
      :object="selectedFile"
      :show="showDeleteAttachmentModal"
      type="file"
      @closeConfirmModal="closeDeleteAttachmentModal"
  ></ConfirmRemoveAttachment>

  <ConfirmRemoveAttachment
      :object="selectedLink"
      :show="showDeleteLinkModal"
      type="link"
      @closeConfirmModal="closeDeleteLinkModal"
  ></ConfirmRemoveAttachment>

</div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import ValidationForm from '@/views/Validations/ValidationForm.vue';
  import ValidationList from '@/views/Validations/ValidationList.vue';
  import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
  import Datepicker from 'vuejs-datepicker';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';


  export default {
    name: 'DataProcessorAgreement',
    data: function() {
      return {
        form: {
          data_processor_agreement: [],
          inspection: [],
          third_country_contract_type: [],
          dpa_description: '',
          countries: [],
          dpa_updated_at: '',
        },
        description: '',
        error: '',
        loadingItems: false,
        showForm: false,
        icItems: [],
        inspectionItems: [
          { name: this.$t('maintenance.third_parties.inspection.no'), value: 'No' },
          { name: this.$t('maintenance.third_parties.inspection.external_audit'), value: 'External audit' },
          { name: this.$t('maintenance.third_parties.inspection.site_visit'), value: 'Site visit' },
          { name: this.$t('maintenance.third_parties.inspection.third-party_self-audit'), value: 'Third-party self-audit' }
        ],
        dpaItems: [
          { name: this.$t('maintenance.third_parties.data_processor_agreement.no'), value: 'No' },
          { name: this.$t('maintenance.third_parties.data_processor_agreement.yes'), value: 'Yes' },
          { name: this.$t('maintenance.third_parties.data_processor_agreement.n/a'), value: 'N/A' }
        ],
        thirdCountryContractTypes: [
          { name: this.$t('maintenance.third_parties.third_country_contract_type.contract_model_clause'), value: 'Contract model clause' },
          // { name: this.$t('maintenance.third_parties.third_country_contract_type.eu/us_privacy_shield'), value: 'EU/US Privacy Shield' },
          { name: this.$t('maintenance.third_parties.third_country_contract_type.eu_us_privacy_framework'), value: 'EU US Data Privacy Framework' },
          { name: this.$t('maintenance.third_parties.third_country_contract_type.adequacy_decision'), value: 'Adequacy decision' },
          { name: this.$t('maintenance.third_parties.third_country_contract_type.binding_corporate_rules'), value: 'Binding corporate rules' },
          { name: this.$t('maintenance.third_parties.third_country_contract_type.no_third_country_transfers'), value: 'No third country transfers' }
        ],
        selectedCountries: [],
        countries: [],
        thirdParty: {},
        showInput: false,
        inputLink: '',
        inputLinkName: '',
        selectedFile: {},
        selectedLink: {},
        showDeleteAttachmentModal: false,
        showDeleteLinkModal: false,
        myGuides: [],
        myTasks: []
      }
    },
    props: {
      object: {
        type: Object
      },
      showOnTask: {},
      tasks: {
        type: Array
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    components: {
      ValidationForm,
      ValidationList,
      Datepicker,
      ConfirmRemoveAttachment,
      GuideButton,
      UpdateSingleTaskModal
    },
    watch: {
      object: function (newValue) {
        if (newValue && newValue.id) {
          this.thirdParty = newValue
          this.loadTasks('update_data_processor_agreement');
          this.loadContent();
        }
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.thirdParty.id && this.$store.state.updatedObject.who_am_i == this.thirdParty.who_am_i) {
              this.thirdParty = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_data_processor_agreement')
        }
      },
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      reloadContent() {
        this.loadTasks('update_data_processor_agreement');
        this.loadContent();
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.thirdParty.who_am_i,
              model_id: this.thirdParty.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Third party' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Third party',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      openDeleteAttachmentModal(object) {
        this.selectedFile = object
        this.showDeleteAttachmentModal = true
      },
      closeDeleteAttachmentModal () {
        this.selectedFile = {}
        this.showDeleteAttachmentModal = false;
        this.$emit('updated');
      },
      openDeleteLinkModal(object) {
        this.selectedLink = object
        this.showDeleteLinkModal = true
      },
      closeDeleteLinkModal () {
        this.selectedLink = {}
        this.showDeleteLinkModal = false;
        this.$emit('updated');
      },
      resetFile() {
        let imagefile = document.querySelector('#file');
        imagefile.value = '';
      },
      handleLink() {
        let self = this;
        let formData = new FormData();

        if (!this.inputLink.startsWith('http')) {
          this.inputLink = 'http://' + this.inputLink
        }

        formData.append("url", this.inputLink);
        formData.append("name", this.inputLinkName);
        formData.append("model_type", this.thirdParty.who_am_i);
        formData.append("model_id", this.thirdParty.id);

        piincHttp.post('links', formData).then(function() {
          self.$toastr('success', self.$t('maintenance.third_parties.link_add_success'))
          self.$emit('updated');
          self.inputLink = ''
          self.inputLinkName = ''
          self.showInput = false
        }, function() {})
      },
      downloadMedia(object){
        piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = object.file_name;

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});
      },
      onFileChange(file) {
        let files = file.target.files;
        let self = this;
        let formData = new FormData();
        let sizeError = false;

        for (var i = 0; i < files.length; i++) {
          // Equal to 10 MB
          if (files[i].size > 10485760) {
            sizeError = true;
          }
          formData.append('document[]', files[i]);
        }

        formData.append("name", "documents");
        formData.append("model_type", this.thirdParty.who_am_i);
        formData.append("model_id", this.thirdParty.id);

        if (sizeError) {
          this.error = this.$t('system.file_size')
        } else {
          piincHttp.post('documents', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function() {
            self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'))
            self.error = ''
            self.$emit('updated');
          }, function(error) {
            self.error = error.data.message;
            if (error.data.errors['document.0']) {
              self.error += ' ' + error.data.errors['document.0'][0]
            }
          })
        }
      },
      formSubmit(evt) {
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        var moment = require('moment');
        evt.preventDefault();

        this.form.data_processor_agreement = this.form.data_processor_agreement ? this.form.data_processor_agreement.value : '';
        this.form.dpa_updated_at = this.form.dpa_updated_at ? moment(this.form.dpa_updated_at).format('YYYY-MM-DD') : ''

        this.form.countries = []
        for (var i = 0; i < this.selectedCountries.length; i++) {
          this.form.countries.push(this.selectedCountries[i].name);
        }

        this.form.inspection = this.form.inspection ? this.form.inspection.value : '';
        this.form.third_country_contract_type = this.form.third_country_contract_type ? this.form.third_country_contract_type.value : '';

        piincHttp.put('third-parties/' + this.thirdParty.slug, this.form).then(function(response) {
          self.thirdParty = response.data
          self.$emit('updated');
          self.showForm = false;
          self.$toastr('success', self.$t('maintenance.third_parties.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      },
      cancelForm() {
        this.form.countries = []
        this.selectedCountries = []
        this.showForm = false
      },
      showFormFunction() {
        this.setDefaultForm(this.thirdParty)
        this.showForm = true
      },
      loadContent() {
        let self = this;
        piincHttp.get('third-parties/' + this.thirdParty.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.thirdParty = response.data;
        }, function() {});
      },
      setDefaultForm(object) {
        let self = this;

        this.form.data_processor_agreement = []
        if (object.data_processor_agreement) {
          this.form.data_processor_agreement = _.find(this.dpaItems, function(item) {
            return item.value === object.data_processor_agreement;
          });
        }

        self.selectedCountries = [];
        self.form.countries = []
        _.forEach(object.countries, function (value, key) {
          _.find(self.countries, function(item) {
            if (item.name == value) {
              self.selectedCountries.push(item)
            }
          });
        })

        this.form.third_country_contract_type = []
        if (object.third_country_contract_type) {
          this.form.third_country_contract_type = _.find(this.thirdCountryContractTypes, function(item) {
            return item.value === object.third_country_contract_type;
          });
        }

        this.form.inspection = []
        if (object.inspection) {
          this.form.inspection = _.find(this.inspectionItems, function(item) {
            return item.value === object.inspection;
          });
        }

        this.form.dpa_updated_at = object.dpa_updated_at;
        this.form.dpa_description = object.dpa_description;
      },
      loadCountries() {
        let self = this;
        piincHttp.get('countries-with-flag').then(function(response) {
          self.countries = response.data
        }, function() {});
      },
    },
    created () {
      if (this.getClient && this.getClient.slug) {

      }
      this.thirdParty = this.object

      if (this.thirdParty && this.thirdParty.id) {
        this.loadTasks('update_data_processor_agreement');
      }
      this.loadCountries()
      this.loadGuides('update_data_processor_agreement');
    }
  }
</script>
